import { useState } from 'react'
import { Spin, Tooltip } from 'antd'
import { CSVLink } from 'react-csv'
import { utils, writeFile } from 'xlsx'
import CustomModal from 'components/global/CustomModal'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import { ExportEventTicketsResp } from 'types/event/eventAttendance'
import './exportTicketModal.scss'

interface Props {
  data: ExportEventTicketsResp | undefined
  isLoading: boolean
}

const CopyableId = ({ id }: { id: string }) => {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard?.writeText(id)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <Tooltip title={copied ? '✓ Copied' : 'Click to copy'} placement="top">
      <span className="id-chip" onClick={copy}>
        {id}
        <span className="copy-icon">⎘</span>
      </span>
    </Tooltip>
  )
}

const SectionDivider = ({ label, count, first }: { label: string; count: number; first?: boolean }) => (
  <tr className="section-divider">
    <td colSpan={7} style={{ paddingTop: first ? 6 : 18 }}>
      <span className="section-divider-label">
        {label}
        <span className="section-divider-count">{count}</span>
      </span>
    </td>
  </tr>
)

const StatusBadge = ({ status }: { status: string }) => {
  const cls = status === 'active' ? 'status-active' : status === 'inactive' ? 'status-inactive' : 'status-default'
  return <span className={`badge ${cls}`}>{status}</span>
}

const DaysCell = ({ days }: { days: string[] }) =>
  days.length > 0 ? (
    <div className="days-cell">
      {days.map(d => <span key={d} className="day-tag">{d}</span>)}
    </div>
  ) : (
    <span className="empty-cell">—</span>
  )

const ExportTicketReferenceModal = ({ data, isLoading }: Props) => {
  const { exportTicketModalOpen, setExportTicketModalOpen } =
    eventAttendanceStore(store => ({
      exportTicketModalOpen: store.exportTicketModalOpen,
      setExportTicketModalOpen: store.setExportTicketModalOpen,
    }))

  const eventId = data?.eventId ?? ''
  const eventName = data?.eventName ?? ''
  const admission = data?.admissionTickets ?? []
  const additional = data?.additionalOptions ?? []
  const dropin = data?.dropInTickets ?? []

  const allRows = [
    ...admission.map(t => ({ eventId, eventName, ticketType: 'admission', ticketId: t.ticketId, name: t.name, price: t.price, status: t.status, days: t.days.join(', '), allowedAdmissionTicketIds: '' })),
    ...additional.map(t => ({ eventId, eventName, ticketType: 'additional', ticketId: t.ticketId, name: t.name, price: t.price, status: t.status, days: t.days.join(', '), allowedAdmissionTicketIds: t.allowedAdmissionTicketIds.join(', ') })),
    ...dropin.map(t => ({ eventId, eventName, ticketType: 'dropin', ticketId: t.ticketId, name: t.name, price: t.price, status: t.status, days: '', allowedAdmissionTicketIds: '' })),
  ]

  const csvHeaders = [
    { label: 'Event ID', key: 'eventId' },
    { label: 'Event Name', key: 'eventName' },
    { label: 'Ticket Type', key: 'ticketType' },
    { label: 'Ticket ID', key: 'ticketId' },
    { label: 'Name', key: 'name' },
    { label: 'Price', key: 'price' },
    { label: 'Status', key: 'status' },
    { label: 'Days', key: 'days' },
    { label: 'Allowed Admission Ticket IDs', key: 'allowedAdmissionTicketIds' },
  ]

  const colWidths = [{ wch: 28 }, { wch: 32 }, { wch: 12 }, { wch: 28 }, { wch: 30 }, { wch: 8 }, { wch: 10 }, { wch: 44 }, { wch: 44 }]

  const downloadExcel = () => {
    const wb = utils.book_new()
    const ws = utils.json_to_sheet(allRows)
    ws['!cols'] = colWidths
    utils.book_append_sheet(wb, ws, 'All Tickets')
    if (admission.length > 0) {
      const s = utils.json_to_sheet(allRows.filter(r => r.ticketType === 'admission'))
      s['!cols'] = colWidths
      utils.book_append_sheet(wb, s, 'Admission')
    }
    if (additional.length > 0) {
      const s = utils.json_to_sheet(allRows.filter(r => r.ticketType === 'additional'))
      s['!cols'] = colWidths
      utils.book_append_sheet(wb, s, 'Additional')
    }
    if (dropin.length > 0) {
      const s = utils.json_to_sheet(allRows.filter(r => r.ticketType === 'dropin'))
      s['!cols'] = colWidths
      utils.book_append_sheet(wb, s, 'Drop-in')
    }
    writeFile(wb, 'ticket-reference.xlsx')
  }

  const hasData = allRows.length > 0

  const footer = (
    <div className="flex justify-end gap-10px">
      <button className="button btn-borderless-gray" onClick={() => setExportTicketModalOpen(false)}>
        Close
      </button>
      {!isLoading && hasData && (
        <>
          <button className="button btn-borderless-gray" onClick={downloadExcel}>
            Export Excel
          </button>
          <CSVLink
            data={allRows}
            headers={csvHeaders}
            filename="ticket-reference.csv"
            className="button btn-primary"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
          >
            Export CSV
          </CSVLink>
        </>
      )}
    </div>
  )

  return (
    <CustomModal
      openModal={exportTicketModalOpen}
      closeModal={() => setExportTicketModalOpen(false)}
      isCloseModalCustom
      title="Ticket Reference Data"
      width={920}
      footer={footer}
    >
      <div className="ticket-ref-modal">
        {(eventId || eventName) && (
          <div className="ticket-ref-event-info">
            {eventName && (
              <div className="ticket-ref-event-row">
                <span className="ticket-ref-event-label">Event Name</span>
                <span className="ticket-ref-event-value">{eventName}</span>
              </div>
            )}
            {eventId && (
              <div className="ticket-ref-event-row">
                <span className="ticket-ref-event-label">Event ID</span>
                <CopyableId id={eventId} />
              </div>
            )}
          </div>
        )}
        <p className="ticket-ref-hint">
          All tickets for this event — IDs, days, and pricing. Click any ID to copy it to your clipboard.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-40px">
            <Spin />
          </div>
        ) : !hasData ? (
          <div className="empty-state">No tickets found for this event</div>
        ) : (
          <div className="ticket-ref-table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 3, padding: 0 }} />
                  <th>Type</th>
                  <th>Name</th>
                  <th>Ticket ID</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Days</th>
                </tr>
              </thead>
              <tbody>
                {admission.length > 0 && (
                  <>
                    <SectionDivider label="Admission Tickets" count={admission.length} first />
                    {admission.map((t, i) => (
                      <tr key={t.ticketId} className="data-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td className="row-accent accent-admission" />
                        <td><span className="badge badge-admission">Admission</span></td>
                        <td><span className="ticket-name">{t.name}</span></td>
                        <td><CopyableId id={t.ticketId} /></td>
                        <td><span className="price-cell">${t.price}</span></td>
                        <td><StatusBadge status={t.status} /></td>
                        <td><DaysCell days={t.days} /></td>
                      </tr>
                    ))}
                  </>
                )}

                {additional.length > 0 && (
                  <>
                    <SectionDivider label="Additional Options" count={additional.length} />
                    {additional.map((t, i) => (
                      <tr key={t.ticketId} className="data-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td className="row-accent accent-additional" />
                        <td><span className="badge badge-additional">Additional</span></td>
                        <td><span className="ticket-name">{t.name}</span></td>
                        <td><CopyableId id={t.ticketId} /></td>
                        <td><span className="price-cell">${t.price}</span></td>
                        <td><StatusBadge status={t.status} /></td>
                        <td><DaysCell days={t.days} /></td>
                      </tr>
                    ))}
                  </>
                )}

                {dropin.length > 0 && (
                  <>
                    <SectionDivider label="Drop-in Tickets" count={dropin.length} />
                    {dropin.map((t, i) => (
                      <tr key={t.ticketId} className="data-row" style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td className="row-accent accent-dropin" />
                        <td><span className="badge badge-dropin">Drop-in</span></td>
                        <td><span className="ticket-name">{t.name}</span></td>
                        <td><CopyableId id={t.ticketId} /></td>
                        <td><span className="price-cell">${t.price}</span></td>
                        <td><StatusBadge status={t.status} /></td>
                        <td><span className="empty-cell">—</span></td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomModal>
  )
}

export default ExportTicketReferenceModal
